import { FC, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { ChevronUpIcon, ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import MDEditor from '@uiw/react-md-editor';
import PageHeader from '@/components/ui/PageHeader/PageHeader';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import SelectInput from '@/components/functional/SelectInput/SelectInput';
import TextInput from '@/components/functional/TextInput/TextInput';
import AdminConfirmModal from '@/components/admin/AdminConfirmModal';
import {
  useAdminPracticeQuestion,
  useUpdatePracticeQuestion,
  useDeletePracticeQuestion,
} from '@/api/useAdminPracticeQuestions';
import { useAdminQuestionCategories } from '@/api/useAdminQuestionCategories';
import Routes from '@/router/Routes';
import { Database } from '@/_constants/database.types';
import { ContentStatus, CONTENT_STATUS_OPTIONS } from '@/_constants/statusOptions';

type CategoryCode = Database['public']['Enums']['category_code'];

interface FormValues {
  category_code: CategoryCode | null;
  content: string;
  marks: number | '';
  status: ContentStatus;
  model_answer: string;
  hint_content: string;
  mark_scheme_content: string;
  reading_insert_content: string;
  reading_insert_content_2: string;
  has_image: boolean;
}

interface CollapsibleSectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const CollapsibleSection: FC<CollapsibleSectionProps> = ({ title, isOpen, onToggle, children }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors ${isOpen ? 'rounded-t-2xl' : 'rounded-2xl'}`}
      >
        <span className="text-lg font-medium text-gray-900">{title}</span>
        {isOpen ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-500" />
        )}
      </button>
      {isOpen && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
};

const EditPracticeQuestionPage: FC = () => {
  const navigate = useNavigate();
  const { questionId } = useParams<{ questionId: string }>();
  const { data: question, isLoading } = useAdminPracticeQuestion(questionId);
  const { data: categories = [] } = useAdminQuestionCategories();
  const { mutate: updateQuestion, isPending: isSaving } = useUpdatePracticeQuestion();
  const { mutate: deleteQuestion, isPending: isDeleting } = useDeletePracticeQuestion();

  const [basicInfoOpen, setBasicInfoOpen] = useState(true);
  const [contentOpen, setContentOpen] = useState(true);
  const [markSchemeOpen, setMarkSchemeOpen] = useState(false);
  const [modelAnswerOpen, setModelAnswerOpen] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  const [readingInsertOpen, setReadingInsertOpen] = useState(false);
  const [readingInsert2Open, setReadingInsert2Open] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      category_code: null,
      content: '',
      marks: '',
      status: 'draft',
      model_answer: '',
      hint_content: '',
      mark_scheme_content: '',
      reading_insert_content: '',
      reading_insert_content_2: '',
      has_image: false,
    },
  });

  // Populate form when question loads
  useEffect(() => {
    if (question) {
      reset({
        category_code: question.category_code,
        content: question.content || '',
        marks: question.marks || '',
        status: question.status,
        model_answer: question.model_answer || '',
        hint_content: question.hint_content || '',
        mark_scheme_content: question.mark_scheme_content ? JSON.stringify(question.mark_scheme_content, null, 2) : '',
        reading_insert_content: question.reading_insert_content || '',
        reading_insert_content_2: question.reading_insert_content_2 || '',
        has_image: question.has_image || false,
      });
    }
  }, [question, reset]);

  // Build category options from database
  const categoryOptions = [
    { value: '', label: 'Select a category...' },
    ...categories.map((cat) => ({
      value: cat.category_code,
      label: `${cat.category_code} (${cat.name})`,
    })),
  ];

  const handleBack = () => {
    navigate(Routes.ADMIN_PRACTICE_QUESTIONS);
  };

  const onSubmit = (data: FormValues) => {
    if (!questionId) return;

    // Parse mark scheme JSON
    let markScheme: unknown = null;
    if (data.mark_scheme_content.trim()) {
      try {
        markScheme = JSON.parse(data.mark_scheme_content);
      } catch {
        // Invalid JSON, keep as null
      }
    }

    updateQuestion(
      {
        questionId,
        updates: {
          category_code: data.category_code || undefined,
          content: data.content || undefined,
          marks: data.marks ? Number(data.marks) : undefined,
          status: data.status,
          model_answer: data.model_answer || undefined,
          hint_content: data.hint_content || undefined,
          mark_scheme_content: markScheme,
          reading_insert_content: data.reading_insert_content || null,
          reading_insert_content_2: data.reading_insert_content_2 || null,
          has_image: data.has_image,
        },
      },
      {
        onSuccess: () => {
          // Stay on page after save
        },
      }
    );
  };

  const handleDelete = () => {
    if (!questionId) return;

    deleteQuestion(questionId, {
      onSuccess: () => {
        navigate(Routes.ADMIN_PRACTICE_QUESTIONS);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="loading loading-spinner loading-lg text-[#C87941]"></div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-gray-500">Question not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-full pb-8">
      {/* Header */}
      <PageHeader
        title="Edit Practice Question"
        showBackButton
        onBackClick={handleBack}
        rightContent={
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            >
              <TrashIcon className="w-5 h-5" />
              Delete
            </button>
            <LoadingButton
              loading={isSaving}
              onClick={handleSubmit(onSubmit)}
              disabled={!isDirty}
              className="bg-[#0f172a] hover:bg-[#1e293b] text-white border-none px-6"
            >
              Save Changes
            </LoadingButton>
          </div>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mt-6">
        {/* Basic Info Section */}
        <CollapsibleSection
          title="Basic Information"
          isOpen={basicInfoOpen}
          onToggle={() => setBasicInfoOpen(!basicInfoOpen)}
        >
          <div className="grid grid-cols-3 gap-6">
            {/* Category Code */}
            <div>
              <Controller
                name="category_code"
                control={control}
                rules={{ required: 'Category is required' }}
                render={({ field }) => (
                  <SelectInput
                    value={field.value || ''}
                    onChange={(value) => field.onChange(value || null)}
                    options={categoryOptions}
                    label="Category Code *"
                  />
                )}
              />
              {errors.category_code && <p className="text-sm text-red-600 mt-1">{errors.category_code.message}</p>}
            </div>

            {/* Marks */}
            <div>
              <Controller
                name="marks"
                control={control}
                rules={{
                  required: 'Marks is required',
                  min: { value: 1, message: 'Min 1' },
                  max: { value: 40, message: 'Max 40' },
                }}
                render={({ field }) => (
                  <TextInput
                    type="number"
                    value={field.value}
                    onChange={field.onChange}
                    min={1}
                    max={40}
                    label="Total Marks *"
                    error={errors.marks?.message}
                    bordered
                  />
                )}
              />
            </div>

            {/* Status */}
            <div>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <SelectInput
                    value={field.value}
                    onChange={(value) => field.onChange(value as ContentStatus)}
                    options={CONTENT_STATUS_OPTIONS}
                    label="Status"
                  />
                )}
              />
            </div>
          </div>
        </CollapsibleSection>

        {/* Question Content Section */}
        <CollapsibleSection title="Question Content" isOpen={contentOpen} onToggle={() => setContentOpen(!contentOpen)}>
          <Controller
            name="content"
            control={control}
            rules={{ required: 'Content is required' }}
            render={({ field }) => (
              <div data-color-mode="light">
                <MDEditor
                  value={field.value}
                  onChange={(value) => field.onChange(value || '')}
                  height={300}
                  preview="live"
                />
              </div>
            )}
          />
          {errors.content && <p className="text-sm text-red-600 mt-2">{errors.content.message}</p>}

          <p className="text-xs text-gray-500 mt-2">
            <span className="font-medium">Tip:</span> To embed an image, use markdown syntax:{' '}
            <code className="bg-gray-100 px-1 rounded">![image][imageUrl]</code>{' '}
            for example:{' '}
            <code className="bg-gray-100 px-1 rounded">![image](https://picsum.photos/200/300)</code>
          </p>

          {/* Has Image Checkbox */}
          <div className="mt-4 flex items-center gap-3">
            <input
              type="checkbox"
              id="has_image"
              {...register('has_image')}
              className="w-5 h-5 rounded border-gray-300 text-[#C87941] focus:ring-[#C87941]"
            />
            <label htmlFor="has_image" className="text-sm font-medium text-gray-700">
              Image-based question (Section B)
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-1 ml-8">
            Check this for Section B questions that use an image prompt instead of a narrative prompt
          </p>
        </CollapsibleSection>

        {/* Mark Scheme Section */}
        <CollapsibleSection
          title="Mark Scheme (JSON)"
          isOpen={markSchemeOpen}
          onToggle={() => setMarkSchemeOpen(!markSchemeOpen)}
        >
          <textarea
            {...register('mark_scheme_content')}
            rows={12}
            placeholder='{"levels": [...], "indicative_content": [...]}'
            className="w-full px-4 py-3 font-mono text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C87941] focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-2">Enter valid JSON for the mark scheme structure</p>
        </CollapsibleSection>

        {/* Model Answer Section */}
        <CollapsibleSection
          title="Model Answer"
          isOpen={modelAnswerOpen}
          onToggle={() => setModelAnswerOpen(!modelAnswerOpen)}
        >
          <Controller
            name="model_answer"
            control={control}
            render={({ field }) => (
              <div data-color-mode="light">
                <MDEditor
                  value={field.value}
                  onChange={(value) => field.onChange(value || '')}
                  height={300}
                  preview="live"
                />
              </div>
            )}
          />
        </CollapsibleSection>

        {/* Hint Section */}
        <CollapsibleSection title="Hint" isOpen={hintOpen} onToggle={() => setHintOpen(!hintOpen)}>
          <div>
            <textarea
              {...register('hint_content')}
              rows={6}
              placeholder="Enter a hint to help students approach this question..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C87941] focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-2">
              This hint is shown when students click "Get Hint". If left empty, a hint will be auto-generated by AI on
              first request.
            </p>
          </div>
        </CollapsibleSection>

        {/* Reading Insert Section */}
        <CollapsibleSection
          title="Reading Insert (English)"
          isOpen={readingInsertOpen}
          onToggle={() => setReadingInsertOpen(!readingInsertOpen)}
        >
          <Controller
            name="reading_insert_content"
            control={control}
            render={({ field }) => (
              <div data-color-mode="light">
                <MDEditor
                  value={field.value}
                  onChange={(value) => field.onChange(value || '')}
                  height={300}
                  preview="live"
                />
              </div>
            )}
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-500">
              The extract or source text that students read before answering the question. Each line break (Enter) = one numbered line in the student view.
            </p>
            <button
              type="button"
              onClick={() => setValue('reading_insert_content', '', { shouldDirty: true })}
              className="text-xs text-red-500 hover:text-red-700 ml-4 shrink-0"
            >
              Clear
            </button>
          </div>
        </CollapsibleSection>

        {/* Reading Insert 2 Section */}
        <CollapsibleSection
          title="Reading Insert 2 (English)"
          isOpen={readingInsert2Open}
          onToggle={() => setReadingInsert2Open(!readingInsert2Open)}
        >
          <Controller
            name="reading_insert_content_2"
            control={control}
            render={({ field }) => (
              <div data-color-mode="light">
                <MDEditor
                  value={field.value}
                  onChange={(value) => field.onChange(value || '')}
                  height={300}
                  preview="live"
                />
              </div>
            )}
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-500">
              Optional second source text (e.g. Text 2 for Paper 2 comparison questions). Each line break = one numbered line.
            </p>
            <button
              type="button"
              onClick={() => setValue('reading_insert_content_2', '', { shouldDirty: true })}
              className="text-xs text-red-500 hover:text-red-700 ml-4 shrink-0"
            >
              Clear
            </button>
          </div>
        </CollapsibleSection>
      </form>

      {/* Delete Confirmation Modal */}
      <AdminConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        variant="error"
        title="Delete Practice Question"
        message="Are you sure you want to delete this practice question? This action cannot be undone."
        confirmText="Delete"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default EditPracticeQuestionPage;
